'use client';

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from 'components/ui/collapsible';
import { cn } from 'lib/utils';
import {
  BrainIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  DatabaseIcon,
  FileIcon,
  FileSearchIcon,
  PencilIcon,
  SearchIcon,
  TerminalIcon,
  WrenchIcon,
} from 'lucide-react';
import map from 'lodash/map';
import omit from 'lodash/omit';
import type { ComponentProps, ReactNode } from 'react';
import { isValidElement, memo, useState } from 'react';

import { CodeBlock } from './code-block';


// --- ThinkingBlock ---

export type ThinkingBlockProps = ComponentProps<typeof Collapsible> & {
  /** Whether the block is currently streaming/active (keeps it expanded) */
  isActive?: boolean;
};

export const ThinkingBlock = memo(
  ({ className, isActive = false, defaultOpen, children, ...props }: ThinkingBlockProps) => (
    <Collapsible
      className={cn('not-prose mb-2', className)}
      defaultOpen={defaultOpen ?? isActive}
      {...props}
    >
      {children}
    </Collapsible>
  ),
);
ThinkingBlock.displayName = 'ThinkingBlock';

// --- ThinkingBlockTrigger ---

export type ThinkingBlockTriggerProps = ComponentProps<typeof CollapsibleTrigger> & {
  summary: string;
  isActive?: boolean;
  /** Override the default BrainIcon with a tool-specific icon */
  icon?: ReactNode;
};

export const ThinkingBlockTrigger = memo(
  ({ className, summary, isActive = false, icon: _icon, ...props }: ThinkingBlockTriggerProps) => (
    <CollapsibleTrigger
      className={cn(
        'flex w-full items-center gap-2 text-muted-foreground text-sm transition-colors hover:text-foreground py-1',
        className,
      )}
      {...props}
    >
      {/* TODO: fix Shimmer and restore it here. bg-clip-text text-transparent renders
         invisible inside CollapsibleTrigger's flex layout, so we use animate-pulse instead. */}
      <span className={cn(isActive && 'animate-pulse')}>{summary}</span>
      <ChevronDownIcon className="size-3.5 shrink-0 transition-transform [[data-state=closed]>&]:rotate-0 [[data-state=open]>&]:rotate-180" />
    </CollapsibleTrigger>
  ),
);
ThinkingBlockTrigger.displayName = 'ThinkingBlockTrigger';

// --- ThinkingBlockContent ---

export type ThinkingBlockContentProps = ComponentProps<typeof CollapsibleContent>;

export const ThinkingBlockContent = memo(
  ({ className, children, ...props }: ThinkingBlockContentProps) => (
    <CollapsibleContent
      className={cn(
        'ml-2 mt-1',
        'data-[state=closed]:animate-out data-[state=open]:animate-in',
        'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
        className,
      )}
      {...props}
    >
      {children}
    </CollapsibleContent>
  ),
);
ThinkingBlockContent.displayName = 'ThinkingBlockContent';

// --- ThinkingTimelineItem ---

export type ThinkingTimelineItemProps = {
  /** Icon to display on the timeline */
  icon: ReactNode;
  /** Whether this is the last item (hides the connecting line) */
  isLast?: boolean;
  className?: string;
  children: ReactNode;
};

/**
 * Wraps a step in the timeline layout: icon positioned on a vertical
 * connecting line with content to the right.
 */
export const ThinkingTimelineItem = memo(
  ({ icon, isLast = false, className, children }: ThinkingTimelineItemProps) => (
    <div className={cn('relative pl-7 pb-1', className)}>
      {/* Vertical line connecting to the next item */}
      {!isLast && (
        <div className="absolute left-[7px] top-5 bottom-0 w-px bg-border" />
      )}
      {/* Icon positioned on the timeline */}
      <div className="absolute left-0 top-[3px]">
        {icon}
      </div>
      {children}
    </div>
  ),
);
ThinkingTimelineItem.displayName = 'ThinkingTimelineItem';

// --- ThinkingStep ---

// Map normalized tool names to lucide icon components
const TOOL_ICON_MAP: Record<string, typeof BrainIcon> = {
  Bash: TerminalIcon,
  Read: FileIcon,
  Write: PencilIcon,
  Edit: PencilIcon,
  Glob: FileSearchIcon,
  Grep: SearchIcon,
  ToolSearch: SearchIcon,
  query_database: DatabaseIcon,
  query_database_to_file: DatabaseIcon,
  list_tables: DatabaseIcon,
  describe_table: DatabaseIcon,
};

/** Strips MCP namespace prefixes: 'mcp__database__query_database' → 'query_database' */
const normalizeMcpName = (name: string): string =>
  name.match(/^mcp__\w+__(.+)$/)?.[1] ?? name;

/**
 * Returns a React icon element for the given tool name.
 * Falls back to WrenchIcon for unknown tools, BrainIcon when no tool name is given.
 */
export const getToolIcon = (toolName?: string, className = 'size-3.5'): ReactNode => {
  if (!toolName) return <BrainIcon className={className} />;
  const Icon = TOOL_ICON_MAP[normalizeMcpName(toolName)] ?? WrenchIcon;
  return <Icon className={className} />;
};

export type ThinkingStepProps = {
  description: string;
  toolName?: string;
  /** Whether this step is actively running */
  isActive?: boolean;
  /** Whether this step has completed */
  isComplete?: boolean;
  /** Hide the inline icon (use when wrapped in ThinkingTimelineItem which renders the icon separately) */
  hideIcon?: boolean;
  className?: string;
};

export const ThinkingStep = memo(
  ({ description, toolName, isActive = false, isComplete = false, hideIcon = false, className }: ThinkingStepProps) => (
    <div
      className={cn(
        'flex items-center gap-2 text-sm text-muted-foreground py-0.5',
        className,
      )}
    >
      {!hideIcon && <span className="shrink-0">{getToolIcon(toolName)}</span>}
      {/* TODO: fix Shimmer and restore it here. bg-clip-text text-transparent renders
         invisible inside flex layouts, so we use animate-pulse instead. */}
      <span className={cn(isActive && 'animate-pulse')}>{description}</span>
      {isComplete && (
        <CheckIcon className="size-3.5 text-green-600 shrink-0" />
      )}
    </div>
  ),
);
ThinkingStep.displayName = 'ThinkingStep';

// --- ThinkingStepResult ---

export type ThinkingStepResultProps = {
  output: unknown;
  errorText?: string;
  /** Override the default "Result" / "Error" label */
  label?: string;
  /** Whether the result is expanded on initial render (user can still toggle) */
  defaultOpen?: boolean;
  className?: string;
};

/**
 * Checks if the output object has a `sql` key, indicating it's a database
 * query tool input that should be rendered with the SQL statement highlighted.
 */
const hasSqlParam = (output: unknown): output is Record<string, unknown> & { sql: string } =>
  typeof output === 'object' &&
  output !== null &&
  !isValidElement(output) &&
  typeof (output as Record<string, unknown>).sql === 'string';

/** Truncates text to a maximum number of lines. */
const truncateLines = (text: string, maxLines: number): string => {
  const lines = text.split('\n');
  return lines.length > maxLines
    ? lines.slice(0, maxLines).join('\n') + '\n...'
    : text;
};

/**
 * Nested collapsible that shows truncated tool output.
 * Collapsed by default — user clicks "Result" to expand.
 *
 * For database query tools (input with a `sql` key), renders the SQL
 * statement as a syntax-highlighted SQL block with any remaining parameters
 * shown separately.
 */
export const ThinkingStepResult = memo(
  ({ output, errorText, label, defaultOpen = false, className }: ThinkingStepResultProps) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);

    if (!(output || errorText)) return null;

    const MAX_LINES = 20;
    const triggerLabel = label ?? (errorText ? 'Error' : 'Result');

    // Render SQL parameters in a user-friendly way: SQL in its own block,
    // each remaining parameter as a labeled key-value line.
    const renderSqlOutput = (data: Record<string, unknown> & { sql: string }) => {
      const sql = truncateLines(data.sql, MAX_LINES);
      const rest = omit(data, 'sql');
      return (
        <>
          <CodeBlock code={sql} language="sql" />
          {map(rest, (value, key) => (
            <div key={key} className="flex items-baseline gap-1.5 text-xs text-muted-foreground mt-1">
              <span className="shrink-0 font-medium">{key}:</span>
              <span className="font-mono">{typeof value === 'string' ? value : JSON.stringify(value)}</span>
            </div>
          ))}
        </>
      );
    };

    const renderDefaultOutput = () => {
      let formattedOutput: string;
      if (typeof output === 'object' && !isValidElement(output)) {
        formattedOutput = JSON.stringify(output, null, 2);
      } else if (typeof output === 'string') {
        formattedOutput = output;
      } else {
        formattedOutput = String(output ?? '');
      }
      return <CodeBlock code={truncateLines(formattedOutput, MAX_LINES)} language="json" />;
    };

    return (
      <Collapsible open={isOpen} onOpenChange={setIsOpen} className={cn('ml-1 mb-1', className)}>
        <CollapsibleTrigger className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors">
          <ChevronRightIcon
            className={cn(
              'size-3 transition-transform',
              isOpen && 'rotate-90',
            )}
          />
          <span>{triggerLabel}</span>
        </CollapsibleTrigger>
        <CollapsibleContent className="mt-1">
          <div className={cn(
            'rounded-md text-xs max-h-64 overflow-auto',
            errorText && 'bg-destructive/10 text-destructive p-2',
          )}>
            {errorText && <div className="mb-1">{errorText}</div>}
            {!errorText && hasSqlParam(output) && renderSqlOutput(output)}
            {!errorText && !hasSqlParam(output) && renderDefaultOutput()}
          </div>
        </CollapsibleContent>
      </Collapsible>
    );
  },
);
ThinkingStepResult.displayName = 'ThinkingStepResult';
